import React from "react";
import {
  Modal,
  Collapse,
  Alert,
  Button,
  message,
  Typography,
  Tooltip,
} from "antd";
import { CopyOutlined, ExclamationCircleOutlined } from "@ant-design/icons";

/**
 * BulkErrorsModal
 *
 * Modal component to display a list of bulk operation failures. Each error
 * entry should contain at least `message` and may include `httpRequestId`,
 * `groupId` and `displayName` for more useful headings and copyable details.
 *
 * Props:
 * - visible: boolean - whether the modal is open
 * - onClose: function - callback when modal is closed
 * - errors: Array<Object> - list of error objects to display
 *
 * Each error object shape (recommended):
 * {
 *   groupId: string|null,
 *   displayName: string|null,
 *   message: string,
 *   httpRequestId: string|null,
 * }
 */
const BulkErrorsModal = ({
  visible = false,
  onClose = () => {},
  errors = [],
}) => {
  /**
   * copyToClipboard
   * Attempt to copy `text` to the clipboard using the modern Clipboard API.
   * Returns a Promise that resolves to `true` on success, `false` on failure
   * or when no clipboard API is available.
   * @param {string} text
   * @returns {Promise<boolean>}
   */
  const copyToClipboard = (text) => {
    if (navigator?.clipboard?.writeText) {
      return navigator.clipboard
        .writeText(text)
        .then(() => true)
        .catch(() => false);
    }

    return Promise.resolve(false);
  };

  /**
   * formatErrorForCopy
   * Format an error object into the exact two-line plain-text format used for
   * copying into tickets:
   * httpRequestId: <id>,
   * message: <message>
   * @param {Object} err
   * @returns {string}
   */
  const formatErrorForCopy = (err) => {
    const errorRequestId = err.httpRequestId || "";
    const errorMessage = err.message.replace(/\n/g, " ") || "";
    return `httpRequestId: ${errorRequestId},\nmessage: ${errorMessage}`;
  };

  /**
   * handleCopyAll
   * Copy all errors' formatted text to the clipboard and show a success/error
   * toast using Ant Design's `message`.
   */
  const handleCopyAll = async () => {
    const text = errors.map(formatErrorForCopy).join("\n\n");
    const ok = await copyToClipboard(text);
    if (ok) message.success("Copied all error details to clipboard");
    else message.error("Failed to copy all error details");
  };

  /**
   * handleCopySingle
   * Copy a single error to the clipboard and show a toast. Stops event
   * propagation so the Collapse panel doesn't toggle when clicking the copy
   * button.
   * @param {Object} err
   * @param {Event} e
   */
  const handleCopySingle = async (err, e) => {
    if (e && typeof e.stopPropagation === "function") e.stopPropagation();
    const ok = await copyToClipboard(formatErrorForCopy(err));
    if (ok) message.success("Copied error details to clipboard");
    else message.error("Failed to copy to clipboard");
  };

  return (
    <Modal
      open={visible}
      title={
        <span style={{ display: "inline-flex", alignItems: "center" }}>
          <ExclamationCircleOutlined
            style={{ color: "#ff4d4f", marginRight: 8 }}
          />
          <span>
            Bulk operation errors{" "}
            <Typography.Text type="secondary" style={{ fontWeight: "normal" }}>
              ({errors.length} failed)
            </Typography.Text>
          </span>
        </span>
      }
      centered
      onCancel={onClose}
      footer={
        <div style={{ textAlign: "right" }}>
          <Button type="text" icon={<CopyOutlined />} onClick={handleCopyAll}>
            Copy all details
          </Button>
        </div>
      }
      width={760}
    >
      <div style={{ marginBottom: 12 }}>
        <div>
          Some operations failed while processing your bulk request. You can
          review the failed groups below.
        </div>
        <div>
          If the problem persists and the error is unexpected, you can{" "}
          <Button
            type="link"
            style={{ padding: 0, display: "inline-block" }}
            href={
              "https://cern.service-now.com/service-portal?id=functional_element&name=GMS"
            }
            target="_blank"
            rel="noopener noreferrer"
          >
            open a ticket
          </Button>{" "}
          in the service portal to report this issue.
        </div>
      </div>
      <Collapse accordion>
        {errors.map((err, i) => {
          const displayMessage = err.message;

          return (
            <Collapse.Panel
              key={i}
              header={
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <div style={{ fontWeight: 600 }}>
                      {err.displayName || err.groupId || "Group"}
                    </div>
                  </div>
                  <div>
                    <Tooltip title="Copy error details">
                      <Button
                        type="text"
                        icon={<CopyOutlined />}
                        size="small"
                        onClick={(e) => handleCopySingle(err, e)}
                        aria-label="Copy error details"
                        style={{ padding: 0, color: "rgba(0,0,0,0.45)" }}
                      />
                    </Tooltip>
                  </div>
                </div>
              }
            >
              <Alert type="error" message={displayMessage} />
            </Collapse.Panel>
          );
        })}
      </Collapse>
    </Modal>
  );
};

export default BulkErrorsModal;
